import { useQuery } from '@tanstack/react-query'
import {
  Box, Typography, Chip, CircularProgress, Paper,
  Table, TableBody, TableCell, TableHead, TableRow, Link, Tooltip,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { getBugSourceLog } from '../../api/client'
import type { BugSourceResolution } from '../../api/types'

const ACTION_CONFIG: Record<BugSourceResolution['action'], {
  label: string
  color: 'success' | 'warning' | 'error'
  icon: React.ReactElement
}> = {
  Resolved: {
    label: 'Resolved',
    color: 'success',
    icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,
  },
  PartiallyResolved: {
    label: 'Partial',
    color: 'warning',
    icon: <RemoveCircleOutlineIcon sx={{ fontSize: 14 }} />,
  },
  Failed: {
    label: 'Failed',
    color: 'error',
    icon: <ErrorOutlineIcon sx={{ fontSize: 14 }} />,
  },
}

function Abbrev({ sha }: { sha?: string }) {
  if (!sha) return <Typography fontSize={12} color="text.disabled">—</Typography>
  return (
    <Tooltip title={sha}>
      <Typography fontFamily="monospace" fontSize={12}>{sha.slice(0, 10)}</Typography>
    </Tooltip>
  )
}

function WiLink({ id, url }: { id?: number; url?: string }) {
  if (!id) return <Typography fontSize={12} color="text.disabled">—</Typography>
  const text = `#${id}`
  return url
    ? <Link href={url} target="_blank" rel="noopener" sx={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600 }}>{text}</Link>
    : <Typography fontFamily="monospace" fontSize={13} fontWeight={600}>{text}</Typography>
}

export default function BugSourceLogPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['bug-source-log'],
    queryFn: () => getBugSourceLog(50),
    refetchInterval: 5_000,
  })

  const entries = data?.data ?? []

  return (
    <Box>
      <Box mb={2}>
        <Typography variant="h5" fontWeight={600}>Bug Escape Source Tracking</Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          When a Bug moves to "PR Submitted", automatically resolves the source work item and deployment
          from the "Introduced @ SHA" field. Refreshes every 5s.
        </Typography>
      </Box>

      {isLoading ? (
        <CircularProgress />
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: 'grey.50' } }}>
                <TableCell>When</TableCell>
                <TableCell>Bug</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>SHA</TableCell>
                <TableCell>Repo</TableCell>
                <TableCell>Source WI</TableCell>
                <TableCell>Deployment WI</TableCell>
                <TableCell>Date Introduced</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                    No bug source tracking events yet — configure the ADO service hook for
                    "PR Submitted" transitions to start seeing activity here.
                  </TableCell>
                </TableRow>
              ) : entries.map(e => {
                const cfg = ACTION_CONFIG[e.action] ?? ACTION_CONFIG.Failed
                return (
                  <TableRow key={e.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: 12 }}>
                      {new Date(e.resolvedAt).toLocaleString()}
                    </TableCell>

                    <TableCell>
                      <WiLink id={e.bugWorkItemId} url={e.bugUrl} />
                    </TableCell>

                    <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {e.project}
                    </TableCell>

                    <TableCell>
                      <Chip
                        icon={cfg.icon}
                        label={cfg.label}
                        color={cfg.color}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>

                    <TableCell>
                      <Abbrev sha={e.introducedAtSha} />
                    </TableCell>

                    <TableCell sx={{ fontSize: 12, color: 'text.secondary', maxWidth: 160 }}>
                      <Tooltip title={e.sourceRepo ?? ''}>
                        <Typography fontSize={12} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {e.sourceRepo ?? '—'}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      <WiLink id={e.sourceWorkItemId} />
                    </TableCell>

                    <TableCell>
                      <WiLink id={e.deploymentWorkItemId} />
                    </TableCell>

                    <TableCell sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                      {e.dateIntroducedToProduction
                        ? new Date(e.dateIntroducedToProduction).toLocaleDateString()
                        : '—'}
                    </TableCell>

                    <TableCell sx={{ maxWidth: 260 }}>
                      <Tooltip title={e.details} placement="top-start">
                        <Typography fontSize={12} color="text.secondary"
                          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {e.details}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  )
}
